import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
  Divider,
  Rating,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Business,
  Assessment,
  Settings,
  Add,
  Description,
  Star,
  ShoppingCart,
  RequestQuote,
  LocalShipping,
  TrendingUp,
  AccessTime,
  CheckCircle,
  AttachMoney,
  Speed,
  ThumbUp,
  ArrowForward,
  VerifiedUser,
  Warning,
  RateReview
} from '@mui/icons-material';
import api from '../../services/api';
import { brand, status as statusColors, text, bg, border, shadow } from '../../theme/colors';

const MerchantDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingQuoteRequests, setPendingQuoteRequests] = useState(0);
  const [pendingOrders, setPendingOrders] = useState(0);
  const [allOrders, setAllOrders] = useState([]);
  const [recentQuoteRequests, setRecentQuoteRequests] = useState([]);

  useEffect(() => {
    fetchMerchantProfile();
    fetchQuoteRequests();
    fetchOrders();
  }, []);

  const fetchMerchantProfile = async () => {
    try {
      const response = await api.get('/merchants/profile/me');

      if (response.data.success) {
        setProfile(response.data.data);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setProfile(null);
      } else {
        setError('Failed to load merchant profile');
        console.error('Error fetching merchant profile:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchQuoteRequests = async () => {
    try {
      const response = await api.get('/merchant-quotes/merchant/requests');

      if (response.data.success) {
        const requests = response.data.data || [];
        const pending = requests.filter(r => r.status === 'sent');
        setPendingQuoteRequests(pending.length);
        // Store recent requests for activity feed
        setRecentQuoteRequests(requests.slice(0, 5));
      }
    } catch (err) {
      console.error('Error fetching quote requests:', err);
      // Surface the failure — silent zeros look like a quiet account
      setError('Some dashboard data failed to load. Pull to refresh or try again.');
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await api.get('/material-orders');

      if (response.data.success) {
        const orders = response.data.data || [];
        setAllOrders(orders);
        const pending = orders.filter(o => o.status === 'confirmed' || o.status === 'preparing');
        setPendingOrders(pending.length);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Some dashboard data failed to load. Pull to refresh or try again.');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress size={48} sx={{ color: brand.primary }} />
        <Typography variant="body1" sx={{ mt: 2, color: text.secondary }}>
          Loading your dashboard...
        </Typography>
      </Container>
    );
  }

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Tier3': return statusColors.success;
      case 'Tier2': return brand.primary;
      case 'Tier1': return statusColors.warning;
      default: return statusColors.neutral;
    }
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'Tier3': return 'Premium Merchant';
      case 'Tier2': return 'Verified Merchant';
      case 'Tier1': return 'Basic Merchant';
      default: return 'Basic Merchant';
    }
  };

  // Compute analytics from orders
  const completedOrders = allOrders.filter(o => o.payment_status === 'completed');
  const totalRevenue = completedOrders.reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0);
  const deliveredOrders = allOrders.filter(o => o.status === 'delivered' || o.status === 'picked_up');
  const activeOrders = allOrders.filter(o => !['completed', 'cancelled', 'disputed'].includes(o.status));

  const thisMonth = new Date();
  thisMonth.setDate(1);
  const thisMonthOrders = completedOrders.filter(o => new Date(o.updated_at) >= thisMonth);
  const thisMonthRevenue = thisMonthOrders.reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color, onClick, badge }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        border: `1px solid ${border.light}`,
        borderRadius: 3,
        transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s',
        position: 'relative',
        overflow: 'visible',
        '&:hover': onClick ? {
          boxShadow: shadow.md,
          transform: 'translateY(-3px)'
        } : {}
      }}
      onClick={onClick}
    >
      {badge > 0 && (
        <Box sx={{
          position: 'absolute', top: -8, right: -8,
          width: 24, height: 24, borderRadius: '50%',
          bgcolor: statusColors.error, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.7rem', fontWeight: 700,
          boxShadow: shadow.sm
        }}>
          {badge}
        </Box>
      )}
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: text.secondary, fontWeight: 500, mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: text.primary, lineHeight: 1.2 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: text.tertiary, mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{
            width: 44, height: 44, borderRadius: 2.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${color}12`, color: color
          }}>
            <Icon sx={{ fontSize: 24 }} />
          </Box>
        </Box>
        {onClick && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2, pt: 1.5, borderTop: `1px solid ${border.light}` }}>
            <Typography variant="caption" sx={{ color: brand.primary, fontWeight: 600 }}>View Details</Typography>
            <ArrowForward sx={{ fontSize: 14, color: brand.primary }} />
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Welcome Header */}
      <Paper
        elevation={0}
        sx={{
          p: 4, mb: 4,
          background: brand.gradient,
          color: 'white',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative circles */}
        <Box sx={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
        <Box sx={{ position: 'absolute', bottom: -30, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, position: 'relative' }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ letterSpacing: '-0.02em' }}>
              Welcome back, {user?.profile?.firstName || user?.email}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.85 }}>
              {profile ? `Managing ${profile.business_name || 'your business'}` : 'Complete your merchant profile to start selling'}
            </Typography>
          </Box>
          {profile && (
            <Chip
              label={getTierLabel(profile.verification_tier)}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                px: 1,
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Profile Setup CTA */}
      {!profile ? (
        <Paper
          elevation={0}
          sx={{
            p: 5, mb: 4, textAlign: 'center',
            border: `2px dashed ${border.default}`,
            borderRadius: 3,
            bgcolor: bg.subtle
          }}
        >
          <Business sx={{ fontSize: 56, color: brand.primary, mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: text.primary }}>
            Complete Your Merchant Profile
          </Typography>
          <Typography variant="body1" sx={{ color: text.secondary, mb: 3, maxWidth: 460, mx: 'auto' }}>
            Set up your business profile to start receiving quote requests and selling materials on the platform.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            onClick={() => navigate('/merchants/profile/create')}
            sx={{
              background: brand.gradient,
              fontWeight: 600,
              px: 4,
              py: 1.3,
              borderRadius: 2,
              boxShadow: shadow.md,
              transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&:hover': {
                background: brand.gradientHover,
                transform: 'translateY(-2px)',
                boxShadow: shadow.lg
              }
            }}
          >
            Create Merchant Profile
          </Button>
        </Paper>
      ) : (
        <>
          {/* Performance Stats */}
          <Grid container spacing={2.5} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard
                title="Revenue"
                value={formatCurrency(totalRevenue)}
                subtitle={thisMonthRevenue > 0 ? `${formatCurrency(thisMonthRevenue)} this month` : 'No sales this month'}
                icon={AttachMoney}
                color={statusColors.success}
                onClick={() => navigate('/payments')}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard
                title="Quote Requests"
                value={pendingQuoteRequests}
                subtitle="Awaiting your response"
                icon={RequestQuote}
                color={brand.primary}
                onClick={() => navigate('/merchant/quote-requests')}
                badge={pendingQuoteRequests}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard
                title="Active Orders"
                value={activeOrders.length}
                subtitle={`${pendingOrders} need attention`}
                icon={LocalShipping}
                color={statusColors.warning}
                onClick={() => navigate('/merchant/orders')}
                badge={pendingOrders}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard
                title="Rating"
                value={profile.rating_average ? parseFloat(profile.rating_average).toFixed(1) : '—'}
                subtitle={`${profile.total_ratings || 0} reviews`}
                icon={Star}
                color="#fbbf24"
                onClick={() => navigate('/merchants/reviews')}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard
                title="Completed"
                value={profile.total_orders_completed || completedOrders.length}
                subtitle="Orders fulfilled"
                icon={CheckCircle}
                color={statusColors.success}
                onClick={() => navigate('/merchant/orders?filter=completed')}
              />
            </Grid>
          </Grid>

          {/* Performance Metrics + Quick Actions row */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Performance Metrics */}
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{ p: 3, border: `1px solid ${border.light}`, borderRadius: 3, height: '100%' }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: text.primary }}>
                    Business Performance
                  </Typography>
                  <Chip
                    label={profile.verification_status || 'Pending'}
                    size="small"
                    icon={profile.verification_status === 'Verified' ? <VerifiedUser sx={{ fontSize: 14 }} /> : <AccessTime sx={{ fontSize: 14 }} />}
                    sx={{
                      bgcolor: profile.verification_status === 'Verified' ? statusColors.successBg : statusColors.warningBg,
                      color: profile.verification_status === 'Verified' ? statusColors.success : statusColors.warning,
                      fontWeight: 600,
                      '& .MuiChip-icon': { color: 'inherit' }
                    }}
                  />
                </Box>

                <Grid container spacing={2.5}>
                  {/* On-time Delivery */}
                  <Grid item xs={6}>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: bg.subtle }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Speed sx={{ fontSize: 18, color: brand.primary }} />
                        <Typography variant="caption" sx={{ color: text.secondary, fontWeight: 500 }}>On-Time Delivery</Typography>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: text.primary }}>
                        {profile.on_time_delivery_rate ? `${parseFloat(profile.on_time_delivery_rate).toFixed(0)}%` : '—'}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={parseFloat(profile.on_time_delivery_rate || 0)}
                        sx={{
                          mt: 1, height: 4, borderRadius: 2,
                          bgcolor: `${brand.primary}15`,
                          '& .MuiLinearProgress-bar': { bgcolor: brand.primary, borderRadius: 2 }
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Quote Acceptance Rate */}
                  <Grid item xs={6}>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: bg.subtle }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ThumbUp sx={{ fontSize: 18, color: statusColors.success }} />
                        <Typography variant="caption" sx={{ color: text.secondary, fontWeight: 500 }}>Quote Acceptance</Typography>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: text.primary }}>
                        {profile.quote_acceptance_rate ? `${parseFloat(profile.quote_acceptance_rate).toFixed(0)}%` : '—'}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={parseFloat(profile.quote_acceptance_rate || 0)}
                        sx={{
                          mt: 1, height: 4, borderRadius: 2,
                          bgcolor: `${statusColors.success}15`,
                          '& .MuiLinearProgress-bar': { bgcolor: statusColors.success, borderRadius: 2 }
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Avg Response Time */}
                  <Grid item xs={6}>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: bg.subtle }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <AccessTime sx={{ fontSize: 18, color: statusColors.info }} />
                        <Typography variant="caption" sx={{ color: text.secondary, fontWeight: 500 }}>Avg Response Time</Typography>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: text.primary }}>
                        {profile.quote_response_time_avg
                          ? `${parseFloat(profile.quote_response_time_avg).toFixed(0)}h`
                          : '—'}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Disputes */}
                  <Grid item xs={6}>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: bg.subtle }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Warning sx={{ fontSize: 18, color: profile.unresolved_disputes > 0 ? statusColors.error : statusColors.neutral }} />
                        <Typography variant="caption" sx={{ color: text.secondary, fontWeight: 500 }}>Open Disputes</Typography>
                      </Box>
                      <Typography variant="h5" sx={{
                        fontWeight: 700,
                        color: profile.unresolved_disputes > 0 ? statusColors.error : text.primary
                      }}>
                        {profile.unresolved_disputes || 0}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Quick Actions */}
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{ p: 3, border: `1px solid ${border.light}`, borderRadius: 3, height: '100%' }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: text.primary, mb: 2.5 }}>
                  Quick Actions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    { label: 'Quote Requests', desc: `${pendingQuoteRequests} pending`, icon: <RequestQuote />, path: '/merchant/quote-requests', color: brand.primary },
                    { label: 'Manage Orders', desc: `${pendingOrders} need action`, icon: <LocalShipping />, path: '/merchant/orders', color: statusColors.warning },
                    { label: 'Business Documents', desc: `${profile.document_counts?.total || 0} uploaded`, icon: <Description />, path: '/merchants/documents', color: statusColors.info },
                    { label: 'Customer Reviews', desc: `${profile.total_ratings || 0} reviews`, icon: <RateReview />, path: '/merchants/reviews', color: '#fbbf24' },
                    { label: 'Edit Profile', desc: 'Business information', icon: <Settings />, path: '/merchants/profile', color: statusColors.neutral },
                  ].map((action) => (
                    <Card
                      key={action.path}
                      elevation={0}
                      sx={{
                        cursor: 'pointer',
                        border: `1px solid ${border.light}`,
                        borderRadius: 2,
                        transition: 'transform 0.15s, box-shadow 0.2s',
                        '&:hover': { boxShadow: shadow.sm, transform: 'translateX(4px)' }
                      }}
                      onClick={() => navigate(action.path)}
                    >
                      <CardContent sx={{ p: '12px 16px !important', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                          width: 36, height: 36, borderRadius: 2,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: `${action.color}12`, color: action.color,
                          flexShrink: 0
                        }}>
                          {action.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: text.primary }}>{action.label}</Typography>
                          <Typography variant="caption" sx={{ color: text.tertiary }}>{action.desc}</Typography>
                        </Box>
                        <ArrowForward sx={{ fontSize: 16, color: text.tertiary }} />
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Recent Activity */}
          {recentQuoteRequests.length > 0 && (
            <Paper
              elevation={0}
              sx={{ p: 3, border: `1px solid ${border.light}`, borderRadius: 3 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: text.primary }}>
                  Recent Quote Requests
                </Typography>
                <Button
                  size="small"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/merchant/quote-requests')}
                  sx={{ color: brand.primary, fontWeight: 600 }}
                >
                  View All
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {recentQuoteRequests.map((req) => {
                  const isUrgent = req.quote_deadline && new Date(req.quote_deadline) < new Date(Date.now() + 2 * 86400000);
                  return (
                    <Box
                      key={req.id}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        p: 2, borderRadius: 2, bgcolor: bg.subtle,
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                        '&:hover': { bgcolor: bg.hover }
                      }}
                      onClick={() => navigate(`/merchant/quote-request/${req.id}`)}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: text.primary }}>
                            {req.project_title || 'Quote Request'}
                          </Typography>
                          {req.status === 'sent' && (
                            <Chip label="New" size="small" sx={{ bgcolor: `${brand.primary}15`, color: brand.primary, fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                          )}
                          {isUrgent && (
                            <Chip label="Urgent" size="small" sx={{ bgcolor: statusColors.errorBg, color: statusColors.error, fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: text.tertiary }}>
                          {req.client_name || 'Client'} &middot; {req.material_categories?.join(', ') || req.request_type}
                          {req.quote_deadline && ` &middot; Due ${new Date(req.quote_deadline).toLocaleDateString()}`}
                        </Typography>
                      </Box>
                      <ArrowForward sx={{ fontSize: 16, color: text.tertiary }} />
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          )}
        </>
      )}
    </Container>
  );
};

export default MerchantDashboard;
